import type { ExchangeRates, RevolutAccount, RevolutAccountScheme } from "@ravnar/api-contract";

import { convertTotals, ratesAgeInDays, type Conversion } from "./exchange-rates";

/** What each way of paying into an account is called, as the Revolut page names it. */
export const REVOLUT_SCHEME_LABELS: Readonly<Record<RevolutAccountScheme, string>> = {
	"UK.OBIE.IBAN": "IBAN",
	"UK.OBIE.SortCodeAccountNumber": "Sort code",
	"US.RoutingNumberAccountNumber": "Routing number",
	"US.BranchCodeAccountNumber": "Branch code",
	"UK.Revolut.InternalAccountId": "Revolut account",
};

/** What each kind of account is called. */
export const REVOLUT_SUBTYPE_LABELS: Readonly<Record<RevolutAccount["subtype"], string>> = {
	CurrentAccount: "Current account",
	Savings: "Savings",
	CreditCard: "Card",
};

/** The order in which the ways of paying in are preferred: the one a person would give to be paid. */
const SCHEME_PREFERENCE: readonly RevolutAccountScheme[] = [
	"UK.OBIE.IBAN",
	"UK.OBIE.SortCodeAccountNumber",
	"US.RoutingNumberAccountNumber",
	"US.BranchCodeAccountNumber",
];

/**
 * How to pay into an account, as the page shows it: an IBAN in groups of four ("IBAN GB00 REVO 0099 6912 3456 01"), a
 * sort code and account number ("Sort code 80-20-01 · 10203345") or the others as Revolut writes them. Undefined for an
 * account Revolut gives no way to pay into, which is how it lists a savings pocket.
 */
export function revolutPaidInBy(account: RevolutAccount): string | undefined {
	const found = SCHEME_PREFERENCE.map(scheme => account.identifications.find(entry => entry.scheme === scheme)).find(
		entry => entry !== undefined,
	);
	if (found === undefined) {
		return;
	}
	const { scheme, identification } = found;
	if (scheme === "UK.OBIE.IBAN") {
		return `IBAN ${identification.replaceAll(/(.{4})(?=.)/g, "$1 ")}`;
	}
	if (scheme === "UK.OBIE.SortCodeAccountNumber" && /^\d{14}$/.test(identification)) {
		const sortCode = identification.slice(0, 6).replaceAll(/(\d{2})(?=\d)/g, "$1-");
		return `Sort code ${sortCode} · ${identification.slice(6)}`;
	}
	return `${REVOLUT_SCHEME_LABELS[scheme]} ${identification}`;
}

/** One currency's total. */
export interface CurrencyTotal {
	readonly currency: string;
	readonly amount: number;
}

/**
 * The order cash in several currencies is listed in while nothing converts it: the organization's primary currency
 * first (Settings › Identity), then the rest by code. Never by amount — amounts in different currencies cannot be
 * compared without exchange rates — so the order holds however the balances move.
 */
export function byCashCurrency(primary: string | undefined): (a: string, b: string) => number {
	return (a, b) => Number(b === primary) - Number(a === primary) || a.localeCompare(b);
}

/**
 * Each currency's total across the accounts, to the cent, each currency apart and none converted, in `byCashCurrency`
 * order. An account Revolut gave no balance for adds nothing, and one that is owed takes its amount away.
 */
export function revolutCashTotals(accounts: readonly RevolutAccount[], primary?: string): CurrencyTotal[] {
	const totals = new Map<string, number>();
	for (const { balance } of accounts) {
		if (balance !== undefined) {
			totals.set(balance.currency, (totals.get(balance.currency) ?? 0) + Number(balance.amount));
		}
	}
	const order = byCashCurrency(primary);
	return [...totals]
		.map(([currency, amount]) => ({ currency, amount: Math.round(amount * 100) / 100 }))
		.toSorted((a, b) => order(a.currency, b.currency));
}

/** A row of the cash table: an account, or the total of a currency several accounts hold. */
export type CashRow =
	| { readonly kind: "account"; readonly account: RevolutAccount }
	| { readonly kind: "total"; readonly currency: string; readonly amount: number; readonly accounts: number };

/**
 * The cash table's rows: the accounts grouped by currency, the groups in `order` (a currency it leaves out comes last,
 * by code), each group's accounts in Revolut's order, and a group where two or more accounts have a balance followed by
 * its total. An account with no balance is listed under its own currency.
 */
export function revolutCashRows(accounts: readonly RevolutAccount[], order: readonly string[]): CashRow[] {
	// Grouped by hand: `Object.groupBy` is newer than the browsers the app still serves.
	const groups = new Map<string, RevolutAccount[]>();
	const currencies: string[] = [];
	for (const account of accounts) {
		const currency = account.balance?.currency ?? account.currency;
		const group = groups.get(currency);
		if (group === undefined) {
			groups.set(currency, [account]);
			currencies.push(currency);
		} else {
			group.push(account);
		}
	}
	const totals = new Map(revolutCashTotals(accounts).map(total => [total.currency, total.amount]));
	const rank = (currency: string) => {
		const index = order.indexOf(currency);
		return index === -1 ? order.length : index;
	};
	return currencies
		.toSorted((a, b) => rank(a) - rank(b) || a.localeCompare(b))
		.flatMap(currency => {
			const members = groups.get(currency) ?? [];
			const rows: CashRow[] = members.map(account => ({ kind: "account", account }));
			const counted = members.filter(account => account.balance !== undefined).length;
			const amount = totals.get(currency);
			return amount !== undefined && counted > 1
				? [...rows, { kind: "total", currency, amount, accounts: counted }]
				: rows;
		});
}

/** Why cash in several currencies is shown unconverted. */
export type UnconvertedReason = "no-rates" | "no-primary-currency" | "no-rate-for-any";

/**
 * How Finance shows the organization's cash: nothing to show; one currency's total, exact; several currencies converted
 * into the primary currency at the reference rates; or several listed apart, when they cannot be converted.
 */
export type CashPosition =
	| { readonly kind: "none" }
	| { readonly kind: "single"; readonly total: CurrencyTotal }
	| {
			readonly kind: "converted";
			readonly conversion: Conversion;
			/** Whether anything was converted from another currency, so the total is an estimate: ≈. */
			readonly approximate: boolean;
			/** The day of the rates, and how many days before today it was. */
			readonly date: string;
			readonly ageInDays: number;
	  }
	| { readonly kind: "unconverted"; readonly totals: readonly CurrencyTotal[]; readonly reason: UnconvertedReason };

/**
 * Where the organization's cash stands, to show: one currency's total is exact, whatever the primary currency; several
 * are converted into the primary currency (Settings › Identity) at the reference rates, or listed apart in
 * `byCashCurrency` order when there are no rates, no primary currency, or no rate for any of them.
 */
export function cashPosition(
	accounts: readonly RevolutAccount[],
	rates: ExchangeRates | undefined,
	primary: string | undefined,
	now: number,
): CashPosition {
	return totalsPosition(revolutCashTotals(accounts, primary), rates, primary, now);
}

/**
 * Where any totals in one currency or several stand, to show, as `cashPosition` shows the cash: nothing; one currency's
 * total, exact; several converted into the primary currency at the reference rates; or several listed apart, in the
 * order they are given, when they cannot be converted. Finance's cards of what is owed show their sums this way too.
 */
export function totalsPosition(
	totals: readonly CurrencyTotal[],
	rates: ExchangeRates | undefined,
	primary: string | undefined,
	now: number,
): CashPosition {
	const [first] = totals;
	if (first === undefined) {
		return { kind: "none" };
	}
	if (totals.length === 1) {
		return { kind: "single", total: first };
	}
	if (rates?.status !== "available") {
		return { kind: "unconverted", totals, reason: "no-rates" };
	}
	if (primary === undefined) {
		return { kind: "unconverted", totals, reason: "no-primary-currency" };
	}
	const conversion = convertTotals(totals, rates, primary);
	if (conversion.converted.length === 0) {
		return { kind: "unconverted", totals, reason: "no-rate-for-any" };
	}
	return {
		kind: "converted",
		conversion,
		approximate: conversion.converted.some(each => each.currency !== primary),
		date: rates.date,
		ageInDays: ratesAgeInDays(rates.date, now),
	};
}
